import { ManagementSubProductSubscriptionDTO, ManagementSubscriptionDTO } from '@iota/inh-core-v1-management-client';
import { EmployeeCaseEntity, EmployeeRequestEntity } from '../store/entity-models';

export function subscriptionIsAssociatedToCase(subscription: ManagementSubscriptionDTO, employeeCase: EmployeeCaseEntity): boolean {
    if (employeeCase?.employeeProductSubscriptionIds?.includes(subscription.id)) {
        return true;
    }

    const subSubscriptionIds = collectSubSubscriptionIds(subscription.subSubscriptions);

    return !!employeeCase?.employeeSubProductSubscriptionIds?.some((id) => subSubscriptionIds.includes(id));
}

function collectSubSubscriptionIds(subSubscriptions: ManagementSubProductSubscriptionDTO[]): string[] {
    return (subSubscriptions ?? []).flatMap((subSubscription) => [
        subSubscription.id,
        ...collectSubSubscriptionIds(subSubscription.subSubscriptions)
    ]);
}

/**
 * Category of the requests of a case, otherwise of the account it is booked on. Returns undefined
 * while an assigned account cannot be resolved yet, so it is never labelled as having none.
 */
export function findCaseCategory(
    employeeCase: EmployeeCaseEntity,
    requests: EmployeeRequestEntity[],
    subscriptions: ManagementSubscriptionDTO[]
): string | undefined {
    const categorizedRequest = requests?.find((request) => request?.categoryLevel1 && request?.isFullyCategorized);
    if (categorizedRequest) {
        return categorizedRequest.categoryDescription;
    }

    const subscriptionCategory = findSubscriptionCategory(employeeCase, subscriptions);
    if (subscriptionCategory) {
        return subscriptionCategory;
    }

    const partlyCategorizedRequest = requests?.find((request) => request?.categoryDescription);
    if (partlyCategorizedRequest) {
        return partlyCategorizedRequest.categoryDescription;
    }

    if (caseHasAssignedSubscriptions(employeeCase)) {
        return undefined;
    }

    return $localize`No category, as no account assigned`;
}

function caseHasAssignedSubscriptions(employeeCase: EmployeeCaseEntity): boolean {
    return employeeCase?.employeeSubProductSubscriptionIds?.length > 0 || employeeCase?.employeeProductSubscriptionIds?.length > 0;
}

function findSubscriptionCategory(employeeCase: EmployeeCaseEntity, subscriptions: ManagementSubscriptionDTO[]): string | undefined {
    const subSubscriptionId = employeeCase?.employeeSubProductSubscriptionIds?.[0];
    if (subSubscriptionId) {
        for (const subscription of subscriptions ?? []) {
            const subSubscription = findSubSubscriptionById(subscription.subSubscriptions, subSubscriptionId);
            if (subSubscription) {
                return subscription.productName + ' > ' + subSubscription.subProductName;
            }
        }

        return undefined;
    }

    const subscriptionId = employeeCase?.employeeProductSubscriptionIds?.[0];
    if (!subscriptionId) {
        return undefined;
    }

    return subscriptions?.find((subscription) => subscription.id === subscriptionId)?.productName;
}

export function findSubSubscriptionById(
    subSubscriptions: ManagementSubProductSubscriptionDTO[],
    subSubscriptionId: string
): ManagementSubProductSubscriptionDTO {
    for (const subSubscription of subSubscriptions ?? []) {
        if (subSubscription.id === subSubscriptionId) {
            return subSubscription;
        }

        const foundInSubtree = findSubSubscriptionById(subSubscription.subSubscriptions, subSubscriptionId);
        if (foundInSubtree) {
            return foundInSubtree;
        }
    }

    return undefined;
}
